<template>
  <div class="content-podcast">
    <div class="inner">
      <h2 v-if="data.title" class="title text-2xl" v-html="data.title" />
      <StructuredText
        v-if="data.description"
        class="structured-text description text-lg richtext-content"
        :data="data.description"
        :render-block="renderBlock"
      />
      <iframe
        v-for="(embed, index) in embeds"
        :key="embed.id"
        class="embed"
        :src="embed.src"
        width="100%"
        height="152"
        frameborder="0"
        allowfullscreen
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        :title="iframeTitle(index)"
      ></iframe>
    </div>
  </div>
</template>

<script setup lang="ts">
import { StructuredText } from 'vue-datocms';
import renderBlock from '~/datocms/renderBlock';
import type { ContentPodcastFragment } from '#gql';

const props = defineProps<{
  data: ContentPodcastFragment;
}>();

// Turns a shared Spotify link (https://open.spotify.com/episode/<id>?si=…) into its
// embed equivalent. Already-embedded urls are passed through untouched.
function toEmbedUrl(podcastUrl: string | null | undefined) {
  if (!podcastUrl) {
    return null;
  }

  let url: URL;

  try {
    url = new URL(podcastUrl);
  } catch {
    return null;
  }

  if (url.hostname !== 'open.spotify.com') {
    return null;
  }

  if (url.pathname.startsWith('/embed/')) {
    return url.toString();
  }

  // localized share links carry an /intl-<locale> prefix
  const path = url.pathname.replace(/^\/intl-[a-z]{2}\//, '/');
  const match = path.match(/^\/(episode|show|playlist)\/([a-zA-Z0-9]+)/);

  if (!match) {
    return null;
  }

  const [, type, id] = match;

  return `https://open.spotify.com/embed/${type}/${id}?utm_source=generator`;
}

const embeds = computed(() => {
  return (props.data.podcastsUrlList ?? [])
    .map((it) => ({ id: it.id, src: toEmbedUrl(it.url) }))
    .filter((it): it is { id: string; src: string } => Boolean(it.src));
});

const iframeTitle = (index: number) => {
  const base = props.data.title ?? 'Podcast';

  return embeds.value.length > 1 ? `${base} (${index + 1})` : base;
};
</script>

<style scoped lang="scss">
.content-podcast {
  @include grid-layout();

  & {
    padding: var(--site-inner-padding);
    width: 100%;
    max-width: var(--site-max-width);
  }

  > .inner {
    display: grid;
    gap: var(--spacing-2xs);
    max-width: 900px;

    @media (--xs) {
      grid-column: 1 / -1;
    }

    @media (--xs), (--sm) {
      padding: var(--spacing-sm) 0;
    }

    @media (--sm) {
      grid-column: 2 / 24;
    }

    @media (--md), (--lg) {
      padding: var(--spacing-3xl) 0;
      grid-column: 5 / 21;
    }

    > .embed {
      border-radius: 12px;
      display: block;
      width: 100%;
    }
  }
}
</style>
